import React, { useRef, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { api } from '../lib/api'
import Layout from '../components/Layout'
import { Card, Spinner, ErrorMessage, Button } from '../components/ui'
import TradeEvaluationView from '../components/TradeEvaluationView'
import Paywall from '../components/Paywall'

// Evaluating a trade already persists it as a draft, so this doesn't create
// anything — it names it, which is what promotes it into the saved list and
// spares it from the nightly prune.
function SaveTradeControl({ trade, tradeId }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState('')

  const save = useMutation({
    mutationFn: () => api.saveTrade(tradeId, name),
    onSuccess: () => {
      setEditing(false)
      queryClient.invalidateQueries({ queryKey: ['trade', tradeId] })
      queryClient.invalidateQueries({ queryKey: ['trades'] })
    },
  })

  const remove = useMutation({
    mutationFn: () => api.deleteTrade(tradeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trades'] })
      navigate('/trades')
    },
  })

  if (!trade) return null

  if (editing || !trade.saved) {
    return (
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault()
          save.mutate()
        }}
      >
        <input
          className="min-w-0 flex-1 rounded-md px-3 py-2 text-sm border border-slate-600 bg-slate-900"
          placeholder="Name this trade to save it"
          value={name}
          autoFocus={editing}
          onChange={(e) => setName(e.target.value)}
        />
        <Button type="submit" disabled={!name.trim() || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        {editing && (
          <Button variant="secondary" type="button" onClick={() => setEditing(false)}>
            Cancel
          </Button>
        )}
        <span className="w-full">
          <ErrorMessage error={save.error} />
        </span>
      </form>
    )
  }

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="text-slate-400">Saved as</span>
      <span className="font-medium text-slate-100">{trade.name}</span>
      <button
        type="button"
        onClick={() => {
          setName(trade.name || '')
          setEditing(true)
        }}
        className="text-xs underline text-slate-400"
      >
        Rename
      </button>
      <button
        type="button"
        onClick={() => remove.mutate()}
        disabled={remove.isPending}
        className="text-xs text-slate-500 underline hover:text-[var(--loss)] text-slate-400"
      >
        {remove.isPending ? 'Deleting…' : 'Delete'}
      </button>
      <span className="w-full">
        <ErrorMessage error={remove.error} />
      </span>
    </div>
  )
}

// Sharing mints a revocable token, so this is a switch rather than a one-way
// door. The link is shown in full rather than hidden behind a copy button
// alone: the sharer should be able to see exactly what they are publishing.
function ShareControl({ trade, tradeId }) {
  const queryClient = useQueryClient()
  const inputRef = useRef(null)
  // 'idle' | 'copied' | 'manual', where 'manual' means the browser refused to
  // copy on our behalf and the link has been selected for the user instead.
  const [copyState, setCopyState] = useState('idle')

  const share = useMutation({
    mutationFn: () => api.shareTrade(tradeId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['trade', tradeId] }),
  })

  const unshare = useMutation({
    mutationFn: () => api.unshareTrade(tradeId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['trade', tradeId] }),
  })

  if (!trade?.saved) return null

  const url = trade.share_token && `${window.location.origin}/s/${trade.share_token}`

  const flash = (state) => {
    setCopyState(state)
    setTimeout(() => setCopyState('idle'), 2000)
  }

  // iOS ignores setSelectionRange on a read-only field, so the node is made
  // briefly editable and selected through a Range instead. Both are restored
  // synchronously, so React never sees the difference.
  const selectLink = () => {
    const input = inputRef.current
    if (!input) return false

    const { contentEditable, readOnly } = input
    input.contentEditable = 'true'
    input.readOnly = false

    const range = document.createRange()
    range.selectNodeContents(input)
    const selection = window.getSelection()
    selection.removeAllRanges()
    selection.addRange(range)
    input.setSelectionRange(0, input.value.length)

    input.contentEditable = contentEditable
    input.readOnly = readOnly
    return true
  }

  // Phones are why this is more than one line. navigator.clipboard is absent
  // outside a secure context (any http:// build you'd open on your phone) and
  // in most in-app webviews, and iOS only honours execCommand while the tap
  // that triggered it is still on the stack — so the fallback runs inline
  // rather than from the promise's rejection handler. If nothing works the
  // link is left selected and the user is told to copy it by hand, which beats
  // a button that silently does nothing.
  const copy = () => {
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(url).then(
          () => flash('copied'),
          () => {
            selectLink()
            setCopyState('manual')
          },
        )
        return
      }
    } catch {
      // fall through to the selection-based copy
    }

    let copiedBySelection = false
    if (selectLink()) {
      try {
        copiedBySelection = document.execCommand('copy')
      } catch {
        copiedBySelection = false
      }
    }

    if (copiedBySelection) flash('copied')
    else setCopyState('manual')
  }

  if (!url) {
    return (
      <div className="mt-2">
        <Button
          variant="secondary"
          onClick={() => share.mutate()}
          disabled={share.isPending}
        >
          {share.isPending ? 'Creating link…' : 'Share'}
        </Button>
        <p className="mt-1 text-xs text-slate-500">
          Anyone with the link can see this trade without signing in. Team and
          league names are hidden.
        </p>
        <ErrorMessage error={share.error} />
      </div>
    )
  }

  return (
    <div className="mt-2">
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={inputRef}
          readOnly
          value={url}
          onFocus={selectLink}
          onClick={selectLink}
          className="min-w-0 flex-1 rounded-md px-3 py-1.5 font-mono text-xs border border-slate-600 bg-slate-900"
        />
        <Button type="button" variant="secondary" onClick={copy}>
          {copyState === 'copied' ? 'Copied' : 'Copy'}
        </Button>
        <button
          type="button"
          onClick={() => unshare.mutate()}
          disabled={unshare.isPending}
          className="text-xs underline text-slate-400 hover:text-slate-200"
        >
          {unshare.isPending ? 'Removing…' : 'Stop sharing'}
        </button>
      </div>
      {copyState === 'manual' && (
        <p className="mt-1 text-xs text-amber-300">
          Your browser wouldn’t let us copy for you. The link is selected —
          press and hold it, then choose Copy.
        </p>
      )}
      <ErrorMessage error={unshare.error} />
    </div>
  )
}


export default function TradeResult() {
  const { tradeId } = useParams()

  const trade = useQuery({
    queryKey: ['trade', tradeId],
    queryFn: () => api.trade(tradeId),
  })

  const evaluation = useQuery({
    queryKey: ['evaluation', tradeId],
    queryFn: () => api.evaluation(tradeId),
  })

  const leagueId = trade.data?.trade?.league_id
  const league = useQuery({
    queryKey: ['league', leagueId],
    queryFn: () => api.league(leagueId),
    enabled: !!leagueId,
  })

  if (evaluation.isPending || trade.isPending) {
    return (
      <Layout wide>
        <Spinner label="Evaluating trade…" />
      </Layout>
    )
  }

  // 402 is the meter, not a failure. The response carries the entitlement with
  // it, so the paywall renders the real numbers rather than hardcoding a limit
  // the server may be running an experiment on. Keyed off `code`, never the
  // prose.
  if (evaluation.error?.status === 402) {
    return (
      <Layout wide>
        <Paywall entitlement={evaluation.error.payload?.entitlement} />
      </Layout>
    )
  }

  if (evaluation.error) {
    return (
      <Layout wide>
        <ErrorMessage error={evaluation.error} />
      </Layout>
    )
  }

  const results = evaluation.data?.evaluation?.results
  const weeks = results?.weeks || []
  const exploratory = Boolean(trade.data?.trade?.exploratory)

  return (
    <Layout wide>
      <div className="mb-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-2">
              <h1 className="truncate text-lg font-semibold text-slate-100">
                {trade.data?.trade?.name ||
                  (exploratory ? 'Trade exploration' : 'Trade evaluation')}
              </h1>
              {exploratory && (
                <span className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide border border-slate-600 text-slate-400">
                  Exploration
                </span>
              )}
            </div>
            <p className="text-sm text-slate-400">
              {results?.league?.name}
              {weeks.length > 0 &&
                ` · weeks ${weeks[0].week}–${weeks[weeks.length - 1].week}`}
            </p>
          </div>

          {leagueId && (
            <Link
              to={`/leagues/${leagueId}/${exploratory ? 'explore' : 'trade'}`}
              className="shrink-0 text-xs underline text-slate-400"
            >
              New trade
            </Link>
          )}
        </div>

        <div className="mt-3">
          <SaveTradeControl trade={trade.data?.trade} tradeId={tradeId} />
          <ShareControl trade={trade.data?.trade} tradeId={tradeId} />
        </div>
      </div>

      <TradeEvaluationView
        trade={trade.data?.trade}
        results={results}
        preferredTeamId={league.data?.league?.my_team_id}
      />
    </Layout>
  )
}
